import { IOptionCategory } from '../models/option-category.interface';

/**
 * Narrows every category to the options whose name contains the query, ignoring case. A blank
 * query returns the input untouched. Categories are kept even when they end up empty, so callers
 * that render a "no results" row per category still see it.
 */
export function filterOptionCategories<T>(
  categories: Array<IOptionCategory<T>>,
  query: string
): Array<IOptionCategory<T>> {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return categories;

  return categories.map((category) => ({
    ...category,
    options: category.options?.filter((option) => option.name.toLowerCase().includes(normalizedQuery)) ?? []
  }));
}

/**
 * Same as `filterOptionCategories`, but a non-blank query also drops categories left with no
 * matches. A blank query still returns the input untouched, empty categories included.
 */
export function filterNonEmptyOptionCategories<T>(
  categories: Array<IOptionCategory<T>>,
  query: string
): Array<IOptionCategory<T>> {
  if (!query.trim()) return categories;
  return filterOptionCategories(categories, query).filter((category) => category.options.length > 0);
}

/** Options within each category ordered by display name, locale-aware and case-insensitive. */
export function sortOptionCategoriesByName<T>(categories: Array<IOptionCategory<T>>): Array<IOptionCategory<T>> {
  return categories.map((category) => ({
    ...category,
    options: [...(category.options ?? [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  }));
}
